import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'

import { BusinessEvent, BusinessEventSourceType } from '../../models/BusinessEvent'

import { RootState } from '../store'

interface BusinessEventState {
  isLoading: boolean
  businessEvents: EntityState<BusinessEvent, string>
  pollingEnabled: boolean
}

const businessEventAdapter = createEntityAdapter<BusinessEvent>()

const initialState: BusinessEventState = {
  isLoading: false,
  businessEvents: businessEventAdapter.getInitialState(),
  pollingEnabled: false,
}

const businessEventSlice = createSlice({
  name: 'businessEvent',
  initialState,
  reducers: {
    setBusinessEventsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setBusinessEvents: (state, action: PayloadAction<BusinessEvent[]>) => {
      businessEventAdapter.setAll(state.businessEvents, action.payload)
    },
    startBusinessEventPolling: (state) => {
      state.pollingEnabled = true
    },
    stopBusinessEventPolling: (state) => {
      state.pollingEnabled = false
    },
    resetBusinessEvents: () => initialState,
  },
})

export const { setBusinessEventsLoading, setBusinessEvents, startBusinessEventPolling, stopBusinessEventPolling, resetBusinessEvents } =
  businessEventSlice.actions

const businessEventSelectors = businessEventAdapter.getSelectors((state: RootState) => state.businessEvent.businessEvents)

export const selectBusinessEvents = (state: RootState) => businessEventSelectors.selectAll(state)

export const selectBusinessEventsLoading = (state: RootState) => state.businessEvent.isLoading

export const selectBusinessEventPollingEnabled = (state: RootState) => state.businessEvent.pollingEnabled

export const selectBusinessEventsForAiFeatureId = createSelector(
  [selectBusinessEvents, (_: RootState, aiFeatureId: string) => aiFeatureId],
  (businessEvents, aiFeatureId) =>
    businessEvents.filter(({ source }) => source.type === BusinessEventSourceType.AI_FEATURE_EXECUTION && source.aiFeatureId === aiFeatureId),
)

export const selectBusinessEventsForEmail = createSelector([selectBusinessEvents, (_: RootState, emailId: string) => emailId], (businessEvents, emailId) =>
  businessEvents.filter(({ source }) => source.type === BusinessEventSourceType.EMAIL && source.emailId === emailId),
)

export default businessEventSlice.reducer
